// File: client_serena/src/pages/institutional/WorkPage.js

import React from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";

const workstreams = [
  {
    number: "01",
    status: "In motion",
    title: "Cardiometabolic Prevention & Precision Health",
    text:
      "Building longitudinal evidence for earlier identification and prevention of cardiometabolic risk among African populations, connecting clinical measurements, biomarkers, lifestyle and emerging precision-health approaches.",
    image: "/images/institutional/serena2.png",
  },
  {
    number: "02",
    status: "Building",
    title: "African Health Data & Longitudinal Evidence",
    text:
      "Developing the evidence infrastructure needed to understand health trajectories over time — and to translate African health data into knowledge that can inform prevention and precision care.",
    image: "/images/institutional/serena7.png",
  },
  {
    number: "03",
    status: "Health technology",
    title: "Selense",
    text:
      "Building a health-memory technology that helps people keep a loved one's health story together, understand what is happening and know what matters next.",
    image: "/images/institutional/serena4.png",
    link: "https://selense.serenaspace.com",
    linkLabel: "Explore Selense",
  },
  {
    number: "04",
    status: "Building",
    title: "Clinical & Health-System Activation",
    text:
      "Working around existing clinical and health-system capabilities to design, activate, standardise and measure the pathways required for promising health innovations to become usable care.",
    image: "/images/institutional/serena5.png",
  },
];

const capabilities = [
  "Preventive & precision health",
  "African health data",
  "Biomarkers & genomics",
  "Longitudinal evidence",
  "Translational science",
  "Implementation research",
  "Health technology",
  "Research capacity",
  "Scientific exchange",
  "Pathway design & activation",
  "Measurement & learning",
  "Translation & scale",
];

function WorkPage() {
  return (
    <Box className="serena-page">

      {/* =========================================================
          HERO
          ========================================================= */}
      <Box bg="#F3F0E8">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 16, md: 24, lg: 28 }}
        >
          <Stack spacing={7} maxW="900px">
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.16em"
              textTransform="uppercase"
              color="#087F73"
            >
              Our work
            </Text>

            <Heading
              as="h1"
              fontFamily="'Poppins', sans-serif"
              fontSize={{
                base: "3xl",
                sm: "4xl",
                md: "5xl",
                lg: "6xl",
              }}
              lineHeight="1.04"
              letterSpacing="-0.045em"
              fontWeight="600"
              color="#17211F"
            >
              Building the pathway between health science and better care.
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg", lg: "xl" }}
              lineHeight="1.75"
              color="#4B5753"
              maxW="780px"
            >
              Serena works across science, technology and healthcare
              environments to turn promising health capabilities into
              evidence, usable tools, working pathways and measurable
              outcomes.
            </Text>
          </Stack>
        </Container>
      </Box>


      {/* =========================================================
          WORK IN MOTION
          ========================================================= */}
      <Box bg="#FFFFFF">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={{ base: 12, md: 16 }}>
            <Box maxW="720px">
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.15em"
                textTransform="uppercase"
                color="#087F73"
                mb={5}
              >
                Selected work
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "3xl", md: "4xl" }}
                lineHeight="1.1"
                letterSpacing="-0.035em"
                fontWeight="600"
                color="#17211F"
              >
                Work in motion.
              </Heading>

              <Text
                mt={5}
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="#56625E"
              >
                Our work spans the evidence, technology and implementation
                layers required to make preventive and precision healthcare
                more actionable.
              </Text>
            </Box>

            <Stack spacing={{ base: 16, lg: 24 }}>
              {workstreams.map((work, index) => {
                const imageFirst = index % 2 === 0;

                return (
                  <SimpleGrid
                    key={work.number}
                    id={work.number === "04" ? "activation" : undefined}
                    scrollMarginTop={{ base: "90px", md: "100px" }}
                    columns={{ base: 1, lg: 2 }}
                    spacing={{ base: 10, lg: 16 }}
                    alignItems="center"
                  >
                    <Box
                      order={{
                        base: 1,
                        lg: imageFirst ? 1 : 2,
                      }}
                      borderRadius="2xl"
                      overflow="hidden"
                    >
                      <Image
                        src={work.image}
                        alt={work.title}
                        w="100%"
                        h={{ base: "300px", md: "420px", lg: "500px" }}
                        objectFit="cover"
                      />
                    </Box>

                    <Stack
                      order={{
                        base: 2,
                        lg: imageFirst ? 2 : 1,
                      }}
                      spacing={5}
                      maxW="620px"
                    >
                      <Flex align="center" gap={4}>
                        <Text
                          fontSize="sm"
                          fontWeight="700"
                          color="#087F73"
                        >
                          {work.number}
                        </Text>

                        <Box
                          h="1px"
                          w="36px"
                          bg="#CBD4D0"
                        />

                        <Text
                          fontSize="xs"
                          fontWeight="700"
                          letterSpacing="0.1em"
                          textTransform="uppercase"
                          color="#68736F"
                        >
                          {work.status}
                        </Text>
                      </Flex>

                      <Heading
                        as="h2"
                        fontFamily="'Poppins', sans-serif"
                        fontSize={{
                          base: "2xl",
                          md: "3xl",
                          lg: "4xl",
                        }}
                        lineHeight="1.1"
                        letterSpacing="-0.035em"
                        fontWeight="600"
                        color="#17211F"
                      >
                        {work.title}
                      </Heading>

                      <Text
                        fontSize={{ base: "md", md: "lg" }}
                        lineHeight="1.8"
                        color="#56625E"
                      >
                        {work.text}
                      </Text>

                      {work.link && (
                        <Button
                          as="a"
                          href={work.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          alignSelf="flex-start"
                          variant="link"
                          color="#087F73"
                          fontWeight="600"
                          rightIcon={<span aria-hidden="true">→</span>}
                        >
                          {work.linkLabel}
                        </Button>
                      )}
                    </Stack>
                  </SimpleGrid>
                );
              })}
            </Stack>
          </Stack>
        </Container>
      </Box>


      {/* =========================================================
          HOW WE WORK
          ========================================================= */}
      <Box bg="#17211F" color="white">
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
          py={{ base: 20, md: 28 }}
        >
          <SimpleGrid
            columns={{ base: 1, lg: 2 }}
            spacing={{ base: 12, lg: 20 }}
          >
            <Stack spacing={6} maxW="680px">
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.15em"
                textTransform="uppercase"
                color="#A9D8D1"
              >
                How we work
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                lineHeight="1.08"
                letterSpacing="-0.04em"
                fontWeight="600"
              >
                We don't stop at the idea.
              </Heading>

              <Text
                fontSize={{ base: "md", md: "lg" }}
                lineHeight="1.8"
                color="whiteAlpha.700"
              >
                Serena follows promising health capabilities through the
                translational pathway — from discovery and building through
                implementation, measurement and learning.
              </Text>
            </Stack>

            <Stack spacing={0}>
              {[
                ["01", "Discover", "Understand the evidence, context and unmet need."],
                ["02", "Build", "Develop the scientific or technological capability required."],
                ["03", "Implement", "Put the capability into a real-world pathway."],
                ["04", "Measure", "Determine whether the pathway is working."],
                ["05", "Learn", "Use implementation evidence to improve the work."],
                ["06", "Scale", "Translate what works into broader use."],
              ].map(([number, title, text]) => (
                <Flex
                  key={number}
                  gap={5}
                  py={5}
                  borderBottom="1px solid"
                  borderColor="whiteAlpha.200"
                  align="flex-start"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color="#A9D8D1"
                    minW="28px"
                  >
                    {number}
                  </Text>

                  <Box>
                    <Text
                      fontSize="lg"
                      fontWeight="600"
                      mb={1}
                    >
                      {title}
                    </Text>

                    <Text
                      fontSize="sm"
                      lineHeight="1.7"
                      color="whiteAlpha.600"
                    >
                      {text}
                    </Text>
                  </Box>
                </Flex>
              ))}
            </Stack>
          </SimpleGrid>
        </Container>
      </Box>


      {/* =========================================================
          CAPABILITIES
          ========================================================= */}
      <Box bg="#F9FAF8">
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={10}>
            <Box maxW="700px">
              <Text
                fontSize="xs"
                fontWeight="700"
                letterSpacing="0.15em"
                textTransform="uppercase"
                color="#087F73"
                mb={5}
              >
                Across the platform
              </Text>

              <Heading
                fontFamily="'Poppins', sans-serif"
                fontSize={{ base: "3xl", md: "4xl" }}
                lineHeight="1.1"
                letterSpacing="-0.035em"
                fontWeight="600"
                color="#17211F"
              >
                Capabilities that connect the work.
              </Heading>
            </Box>

            <Flex
              wrap="wrap"
              gap={3}
            >
              {capabilities.map((capability) => (
                <Box
                  key={capability}
                  px={5}
                  py={3}
                  bg="white"
                  border="1px solid"
                  borderColor="#DCE2DE"
                  borderRadius="full"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="500"
                    color="#34413D"
                  >
                    {capability}
                  </Text>
                </Box>
              ))}
            </Flex>
          </Stack>
        </Container>
      </Box>


      {/* =========================================================
          FINAL CTA
          ========================================================= */}
      <Box bg="#F3F0E8">
        <Container
          maxW="1200px"
          px={{ base: 5, md: 8 }}
          py={{ base: 20, md: 28 }}
        >
          <Stack spacing={7} maxW="820px">
            <Text
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.15em"
              textTransform="uppercase"
              color="#087F73"
            >
              Work with Serena
            </Text>

            <Heading
              fontFamily="'Poppins', sans-serif"
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              lineHeight="1.08"
              letterSpacing="-0.04em"
              fontWeight="600"
              color="#17211F"
            >
              Have a capability that needs to move from possibility to
              practice?
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              lineHeight="1.8"
              color="#56625E"
              maxW="720px"
            >
              We are interested in working with organisations developing
              evidence, technologies and health innovations that need a
              reliable pathway into real-world use.
            </Text>

            <Button
              as="a"
              href="/partner"
              alignSelf="flex-start"
              size="lg"
              px={7}
              bg="#087F73"
              color="white"
              borderRadius="full"
              fontWeight="600"
              _hover={{
                bg: "#06695F",
              }}
            >
              Partner with Serena
            </Button>
          </Stack>
        </Container>
      </Box>

    </Box>
  );
}

export default WorkPage;